"use client";

import React, { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import {
  Search,
  LayoutDashboard,
  User,
  Compass,
  Briefcase,
  Users,
  DollarSign,
  Shield,
  FileText,
  Clock,
  Award,
  ChevronRight,
  TrendingUp,
  Target,
  AlertTriangle,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/overlays";
import { Badge } from "@/components/ui/feedback";

interface CommandItem {
  name: string;
  category: "navigation" | "work" | "people" | "finance" | "strategy" | "sales";
  path: string;
  description: string;
  icon: React.ComponentType<any>;
}

const COMMANDS: CommandItem[] = [
  {
    name: "Overview Board",
    category: "navigation",
    path: "/overview",
    description: "Executive control board and cash overview",
    icon: LayoutDashboard,
  },
  {
    name: "My Space",
    category: "navigation",
    path: "/my-space",
    description: "Personal cockpit, tasks, timesheets, and goals",
    icon: User,
  },
  {
    name: "Explore Graph",
    category: "navigation",
    path: "/explore",
    description: "Semantic relational company context graph",
    icon: Compass,
  },

  // Work & Operations
  {
    name: "Progetti",
    category: "work",
    path: "/work/projects",
    description: "Manage company projects and watch states",
    icon: Briefcase,
  },
  {
    name: "Task",
    category: "work",
    path: "/work/tasks",
    description: "Task lists and kanban work board",
    icon: FileText,
  },
  {
    name: "Timesheets",
    category: "work",
    path: "/work/timesheets",
    description: "Time tracking and active timesheet logs",
    icon: Clock,
  },
  {
    name: "Documenti",
    category: "work",
    path: "/work/documents",
    description: "RAG knowledge base and documentation",
    icon: FileText,
  },
  {
    name: "Suppliers",
    category: "work",
    path: "/operations/suppliers",
    description: "External providers and vendors",
    icon: Users,
  },
  {
    name: "Assets",
    category: "work",
    path: "/operations/assets",
    description: "Company physical and digital infrastructure",
    icon: Briefcase,
  },
  {
    name: "Iniziative",
    category: "work",
    path: "/operations/initiatives",
    description: "Internal company development tracks",
    icon: TrendingUp,
  },

  // People
  {
    name: "People Directory",
    category: "people",
    path: "/people",
    description: "Team members and collaboration directory",
    icon: Users,
  },
  {
    name: "Capacity Planning",
    category: "people",
    path: "/people/capacity",
    description: "Workload and burnout monitoring",
    icon: Users,
  },
  {
    name: "Time Off",
    category: "people",
    path: "/people/time-off",
    description: "Holidays, leaves, and time off calendar",
    icon: Clock,
  },
  {
    name: "Kudos Board",
    category: "people",
    path: "/people/kudos",
    description: "Peer-to-peer appreciation and peer recognitions",
    icon: Award,
  },

  // Finance & Sales
  {
    name: "Invoices",
    category: "finance",
    path: "/finance/invoices",
    description: "B2B client invoices and payment states",
    icon: DollarSign,
  },
  {
    name: "Costs",
    category: "finance",
    path: "/finance/costs",
    description: "Operational costs and monthly expenses",
    icon: DollarSign,
  },
  {
    name: "Leads Pipeline",
    category: "sales",
    path: "/sales/leads",
    description: "Sales acquisition funnel and opportunities",
    icon: TrendingUp,
  },
  {
    name: "Proposals",
    category: "sales",
    path: "/sales/proposals",
    description: "Commercial proposals and contracts status",
    icon: FileText,
  },
  {
    name: "Sales Clients",
    category: "sales",
    path: "/sales/clients",
    description: "CRM and client profile directory",
    icon: Users,
  },

  // Strategy & Risks
  {
    name: "Obiettivi Strategici (Goals)",
    category: "strategy",
    path: "/strategy/goals",
    description: "OKRs and strategic objectives",
    icon: Target,
  },
  {
    name: "Risks Management",
    category: "strategy",
    path: "/strategy/risks",
    description: "Company risk assessment matrix",
    icon: AlertTriangle,
  },
  {
    name: "SWOT Analysis",
    category: "strategy",
    path: "/strategy/swot",
    description: "Strengths, Weaknesses, Opportunities, Threats",
    icon: Shield,
  },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const router = useRouter();
  const listRef = useRef<HTMLDivElement>(null);

  // Toggle open command palette on Ctrl+K / Cmd+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Listen to custom event toggling from Header or other sources
  useEffect(() => {
    const handleToggle = () => {
      setOpen((o) => !o);
    };
    window.addEventListener("toggle-command-palette", handleToggle);
    return () =>
      window.removeEventListener("toggle-command-palette", handleToggle);
  }, []);

  // Filter commands
  const filteredCommands = COMMANDS.filter((cmd) => {
    const query = searchQuery.toLowerCase();
    return (
      cmd.name.toLowerCase().includes(query) ||
      cmd.category.toLowerCase().includes(query) ||
      cmd.description.toLowerCase().includes(query) ||
      cmd.path.toLowerCase().includes(query)
    );
  });

  // Handle selected item when list changes or filter updates
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  // Keyboard navigation inside the palette
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % filteredCommands.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(
        (prev) =>
          (prev - 1 + filteredCommands.length) % filteredCommands.length,
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredCommands[selectedIndex]) {
        handleNavigate(filteredCommands[selectedIndex].path);
      }
    }
  };

  const handleNavigate = (path: string) => {
    router.push(path);
    setOpen(false);
    setSearchQuery("");
  };

  // Scroll to selected element
  useEffect(() => {
    if (listRef.current) {
      const selectedElement = listRef.current.children[
        selectedIndex
      ] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl p-0 overflow-hidden border border-border bg-card shadow-hover rounded-xl">
        <div className="relative flex items-center border-b border-border px-4 py-3 bg-card-muted/30">
          <Search className="h-4 w-4 mr-3 text-text-muted shrink-0" />
          <input
            type="text"
            className="w-full bg-transparent text-sm text-foreground outline-none placeholder:text-text-muted"
            placeholder="Cerca comandi, pagine o sezioni... (↑/↓ naviga, Enter seleziona)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          <kbd className="hidden sm:inline-flex h-5 items-center gap-0.5 rounded border border-border bg-card px-1.5 font-mono text-[10px] text-text-muted font-medium select-none">
            ESC
          </kbd>
        </div>

        <div className="max-h-[350px] overflow-y-auto p-2 scrollbar-thin">
          {filteredCommands.length === 0 ? (
            <div className="py-8 text-center text-sm text-text-muted select-none">
              Nessun risultato trovato per &ldquo;{searchQuery}&rdquo;
            </div>
          ) : (
            <div ref={listRef} className="space-y-0.5">
              {filteredCommands.map((cmd, index) => {
                const Icon = cmd.icon;
                const isSelected = index === selectedIndex;
                return (
                  <div
                    key={cmd.path}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-colors select-none ${
                      isSelected
                        ? "bg-primary-500/10 text-primary-500"
                        : "hover:bg-card-muted text-text-primary"
                    }`}
                    onClick={() => handleNavigate(cmd.path)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-8 w-8 rounded-lg flex items-center justify-center border transition-colors ${
                          isSelected
                            ? "bg-primary-500/20 border-primary-500/30 text-primary-500"
                            : "bg-card-muted/50 border-border/50 text-text-secondary"
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col text-left">
                        <span className="text-xs font-semibold">
                          {cmd.name}
                        </span>
                        <span className="text-[10px] text-text-muted line-clamp-1">
                          {cmd.description}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge
                        variant={cmd.category as any}
                        className="text-[9px] px-1.5 py-0"
                      >
                        {cmd.category}
                      </Badge>
                      <ChevronRight className="h-3.5 w-3.5 text-text-muted" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-4 py-2 border-t border-border bg-card-muted/20 text-[10px] text-text-muted">
          <span>Usa le frecce della tastiera per navigare</span>
          <div className="flex items-center gap-1.5">
            <span>Seleziona con</span>
            <kbd className="rounded border border-border bg-card px-1 font-mono text-[9px] text-text-secondary">
              ↵
            </kbd>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
